// Traduz a falha de cadastro em uma mensagem que o usuário consegue agir.
//
// O signUp tem duas etapas com formatos de erro completamente diferentes:
//   1. POST /v1/user/signup  → erro do axios (status HTTP + corpo JSON)
//   2. signInWithEmailAndPassword → erro do Firebase (error.code "auth/...")
//
// O handler antigo só checava `error.code === "auth/..."`, que jamais bate na
// etapa 1 — axios usa códigos como "ERR_BAD_REQUEST". Resultado: toda falha do
// backend, inclusive as que já vêm com mensagem pronta e específica, caía no
// texto genérico "Erro ao tentar criar a conta, tente novamente mais tarde!".

import { ACCOUNT_DISABLED_MESSAGE } from "./constants";

const GENERIC =
  "Não foi possível criar sua conta. Tente novamente em alguns instantes.";

// Mensagens do backend que não dizem nada ao usuário — quando vierem, seguimos
// procurando um motivo melhor no restante do corpo da resposta.
const BACKEND_PLACEHOLDERS = new Set([
  "Erro ao realizar cadastro de usuário.",
  "Dados inválidos fornecidos",
  "Internal Server Error",
]);

const FIREBASE_MESSAGES = {
  "auth/email-already-in-use":
    "Este e-mail já está cadastrado. Faça login ou recupere sua senha.",
  "auth/email-already-exists":
    "Este e-mail já está cadastrado. Faça login ou recupere sua senha.",
  "auth/invalid-email": "E-mail inválido. Confira se digitou corretamente.",
  "auth/weak-password":
    "Senha muito fraca. Use pelo menos 8 caracteres, com letras e números.",
  "auth/invalid-password":
    "Senha muito fraca. Use pelo menos 8 caracteres, com letras e números.",
  "auth/operation-not-allowed":
    "O cadastro por e-mail está temporariamente indisponível. Fale com o suporte.",
  "auth/too-many-requests":
    "Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente de novo.",
  "auth/network-request-failed":
    "Sem conexão com o servidor. Verifique sua internet e tente novamente.",
  "auth/user-disabled": ACCOUNT_DISABLED_MESSAGE,
};

const STATUS_MESSAGES = {
  401: "Não foi possível autorizar o cadastro. Recarregue a página e tente novamente.",
  403: "Não foi possível autorizar o cadastro. Recarregue a página e tente novamente.",
  409: "Este e-mail já está cadastrado. Faça login ou recupere sua senha.",
  413: "A imagem enviada é muito grande. Escolha uma foto menor.",
  429: "Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente de novo.",
};

// Procura um código "auth/..." em qualquer lugar do corpo devolvido pela API —
// o controller repassa o erro cru do Firebase Admin, e a profundidade em que
// ele aparece varia conforme como o erro foi serializado.
const findFirebaseCode = (payload) => {
  const candidates = [
    payload?.error?.code,
    payload?.error?.errorInfo?.code,
    payload?.error?.error?.code,
    payload?.code,
  ];
  return candidates.find(
    (code) => typeof code === "string" && code.startsWith("auth/"),
  );
};

const usableMessage = (message) =>
  typeof message === "string" &&
  message.trim() &&
  !BACKEND_PLACEHOLDERS.has(message.trim())
    ? message.trim()
    : null;

export const describeSignupError = (error) => {
  // --- Etapa 2: erro do SDK do Firebase no navegador ---
  if (FIREBASE_MESSAGES[error?.code]) {
    return FIREBASE_MESSAGES[error.code];
  }

  const response = error?.response;

  // --- Sem resposta HTTP: a requisição nem chegou ao servidor ---
  if (!response) {
    if (error?.code === "ERR_NETWORK" || error?.request) {
      return "Sem conexão com o servidor. Verifique sua internet e tente novamente.";
    }
    if (error?.code === "ECONNABORTED") {
      return "O servidor demorou para responder. Tente novamente.";
    }
    // Erros lançados pelo próprio service da API (ex.: regra de maioridade)
    // chegam aqui como Error comum, já com texto voltado ao usuário.
    return usableMessage(error?.message) || GENERIC;
  }

  // --- Etapa 1: erro do backend ---
  const data = response.data;

  // 422: lista de campos reprovados na validação, cada um com mensagem pronta.
  const details = data?.error?.details;
  if (Array.isArray(details) && details.length) {
    const messages = details
      .map((detail) => usableMessage(detail?.message))
      .filter(Boolean);
    if (messages.length) return messages.join(" ");
  }

  // O backend embrulha o erro do Firebase Admin num 500 genérico; se o código
  // original sobreviveu à serialização, ele é muito mais útil que o envelope.
  const firebaseCode = findFirebaseCode(data);
  if (firebaseCode && FIREBASE_MESSAGES[firebaseCode]) {
    return FIREBASE_MESSAGES[firebaseCode];
  }

  const explicit =
    usableMessage(data?.error?.message) || usableMessage(data?.message);
  if (explicit) return explicit;

  if (STATUS_MESSAGES[response.status]) {
    return STATUS_MESSAGES[response.status];
  }

  if (response.status >= 500) {
    return "Nosso servidor está instável no momento. Tente novamente em alguns minutos.";
  }

  return GENERIC;
};

export default describeSignupError;
